const preset = (id, label, detail, values, typography) => ({
  id,
  label,
  detail,
  values,
  ...(typography ? { typography } : {}),
});

// These presets mirror the layout and per-block spacing from the approved
// SCROLL-PRESET-TEMP-121026 project. Project-wide typography (fonts, sizes,
// weights, and line heights) remains untouched except where a preset includes
// an explicit, reviewed typography override.
const PRESETS = {
  rolename: [
    preset("full-gutter", "Full Gutter", "1 column · 100%", {
      double_columns: true,
      text_alignment: "gutter",
      columns: 1,
      column_width: 100,
      name_on_left: false,
      block_inset: "default",
      margin_vertical: 30,
      extra_lines_below: 0,
    }),
    preset("four-up-gutter", "Four up Gutter", "2 columns · gutter", {
      double_columns: true,
      text_alignment: "gutter",
      columns: 2,
      column_width: 50,
      name_on_left: false,
      block_inset: "default",
      margin_vertical: 20,
      extra_lines_below: 0,
    }),
    preset("left-pair", "Left Pair", "2 columns · 30%", {
      double_columns: false,
      text_alignment: "left",
      columns: 2,
      column_width: 30,
      name_on_left: false,
      block_inset: "left",
      margin_vertical: 10,
      extra_lines_below: 11,
    }),
    preset("right-pair", "Right Pair", "2 columns · 30%", {
      double_columns: false,
      text_alignment: "right",
      columns: 2,
      column_width: 30,
      name_on_left: false,
      block_inset: "right",
      margin_vertical: 10,
      extra_lines_below: 11,
    }),
    preset("justified-split", "Justified Split", "1 column · 50%", {
      double_columns: true,
      text_alignment: "justify",
      columns: 1,
      column_width: 50,
      name_on_left: false,
      block_inset: "default",
      margin_vertical: 20,
      extra_lines_below: 1,
    }),
    preset(
      "names-first",
      "Names First",
      "1.2 line · 10 credit height",
      {
        double_columns: false,
        text_alignment: "center",
        columns: 1,
        column_width: 60,
        name_on_left: true,
        block_inset: "left",
        margin_vertical: 20,
        extra_lines_below: 0,
      },
      {
        role: { lineHeight: 1.2, marginBottom: 10 },
        name: { lineHeight: 1.2, marginBottom: 10 },
      },
    ),
    preset("ragged-wide", "Ragged Wide", "1 column · 80%", {
      double_columns: true,
      text_alignment: "ragged",
      columns: 1,
      column_width: 80,
      name_on_left: false,
      block_inset: "default",
      margin_vertical: 0,
      extra_lines_below: 0,
    }),
    preset("side-by-side", "Side by Side", "Gutter · 60%", {
      double_columns: true,
      text_alignment: "gutter",
      columns: 1,
      column_width: 60,
      name_on_left: false,
      block_inset: "sidebyside",
      margin_vertical: 30,
      extra_lines_below: 0,
    }),
  ],
  nameonly: [
    preset("centered-list", "Centered List", "1 column · 100%", {
      bullets: null,
      text_alignment: "center",
      columns: 1,
      column_width: 100,
      balance_columns: null,
      column_flow: "horizontal",
      column_order: null,
      block_inset: "default",
      margin_vertical: 20,
      extra_lines_below: 0,
    }),
    preset("a-z-two-column", "A–Z Two Column", "Left · 2 columns", {
      bullets: null,
      text_alignment: "left",
      columns: 2,
      column_width: 30,
      balance_columns: null,
      column_flow: "horizontal",
      column_order: "ascending",
      block_inset: "default",
      margin_vertical: 20,
      extra_lines_below: 0,
    }),
    preset("z-a-grid", "Z–A Grid", "Justified · 4 columns", {
      bullets: null,
      text_alignment: "justify",
      columns: 4,
      column_width: 17,
      balance_columns: "center",
      column_flow: "vertical",
      column_order: "descending",
      block_inset: "default",
      margin_vertical: 20,
      extra_lines_below: 0,
    }),
    preset("circle-bullets", "Circle Bullets", "Centered · 60%", {
      bullets: "circle",
      text_alignment: "center",
      columns: 1,
      column_width: 60,
      balance_columns: null,
      column_flow: "horizontal",
      column_order: null,
      block_inset: "default",
      margin_vertical: 20,
      extra_lines_below: 0,
    }),
    preset("diamond-bullets", "Diamond Bullets", "Left · 60%", {
      bullets: "diamond",
      text_alignment: "left",
      columns: 1,
      column_width: 60,
      balance_columns: null,
      column_flow: "horizontal",
      column_order: null,
      block_inset: "default",
      margin_vertical: 20,
      extra_lines_below: 0,
    }),
    preset("cross-bullets-right", "Cross Bullets", "Right · 60%", {
      bullets: "cross",
      text_alignment: "right",
      columns: 1,
      column_width: 60,
      balance_columns: null,
      column_flow: "horizontal",
      column_order: null,
      block_inset: "default",
      margin_vertical: 20,
      extra_lines_below: 0,
    }),
    preset("cross-side-by-side", "Side-by-Side", "Cross bullets · left", {
      bullets: "cross",
      text_alignment: "left",
      columns: 1,
      column_width: 61,
      balance_columns: null,
      column_flow: "horizontal",
      column_order: null,
      block_inset: "sidebyside",
      margin_vertical: 20,
      extra_lines_below: 11,
    }),
    preset("right-bullets", "Right Bullets", "Cross bullets · 60%", {
      bullets: "cross",
      text_alignment: "right",
      columns: 1,
      column_width: 60,
      balance_columns: null,
      column_flow: "horizontal",
      column_order: null,
      block_inset: "default",
      margin_vertical: 20,
      extra_lines_below: 0,
    }),
  ],
  blurb: [
    preset("centered-70", "Centered", "70% width", {
      text_alignment: "center",
      column_width: 70,
      block_inset: "default",
      margin_vertical: 20,
      extra_lines_below: 0,
    }),
    preset("left-70", "Left Aligned", "70% width", {
      text_alignment: "left",
      column_width: 70,
      block_inset: "default",
      margin_vertical: 20,
      extra_lines_below: 0,
    }),
    preset("right-70", "Right Aligned", "70% width", {
      text_alignment: "right",
      column_width: 70,
      block_inset: "default",
      margin_vertical: 20,
      extra_lines_below: 0,
    }),
    preset("wide-centered", "Wide Centered", "80% width", {
      text_alignment: "center",
      column_width: 80,
      block_inset: "default",
      margin_vertical: 20,
      extra_lines_below: 0,
    }),
    preset("compact-footer", "Compact Footer", "65% · extra bottom", {
      text_alignment: "center",
      column_width: 65,
      block_inset: "default",
      margin_vertical: 20,
      extra_lines_below: 10,
    }),
    preset("side-by-side", "Side-by-Side", "Left · 60%", {
      text_alignment: "left",
      column_width: 60,
      block_inset: "sidebyside",
      margin_vertical: 20,
      extra_lines_below: 0,
    }),
    preset("narrow-right", "Narrow Right", "Right · 60%", {
      text_alignment: "right",
      column_width: 60,
      block_inset: "default",
      margin_vertical: 20,
      extra_lines_below: 0,
    }),
    preset("closing-card", "Closing Card", "Centered · 50% margin", {
      text_alignment: "center",
      column_width: 60,
      block_inset: "default",
      margin_vertical: 50,
      extra_lines_below: 0,
    }),
  ],
  song: [
    preset("centered-single", "Centered Single", "1 column · 50%", {
      text_alignment: "center",
      columns: 1,
      column_width: 50,
      horizontal_gap: 20,
      balance_columns: null,
      block_inset: "default",
      margin_vertical: 20,
      extra_lines_below: 0,
    }),
    preset("gutter-pair", "Gutter Pair", "2 columns · 50%", {
      text_alignment: "gutter",
      columns: 2,
      column_width: 50,
      horizontal_gap: 20,
      balance_columns: null,
      block_inset: "default",
      margin_vertical: 20,
      extra_lines_below: 0,
    }),
    preset("justified-pair", "Justified Pair", "2 columns · 30%", {
      text_alignment: "justify",
      columns: 2,
      column_width: 30,
      horizontal_gap: 20,
      balance_columns: null,
      block_inset: "default",
      margin_vertical: 20,
      extra_lines_below: 0,
    }),
    preset("left-inset", "Left Inset", "Single · 61%", {
      text_alignment: "center",
      columns: 1,
      column_width: 61,
      horizontal_gap: 20,
      balance_columns: null,
      block_inset: "left",
      margin_vertical: 20,
      extra_lines_below: 0,
    }),
    preset("right-inset", "Right Inset", "Single · 100%", {
      text_alignment: "center",
      columns: 1,
      column_width: 100,
      horizontal_gap: 20,
      balance_columns: null,
      block_inset: "right",
      margin_vertical: 20,
      extra_lines_below: 0,
    }),
    preset("balanced-pair", "Balanced Pair", "Justified · 30%", {
      text_alignment: "justify",
      columns: 2,
      column_width: 30,
      horizontal_gap: 20,
      balance_columns: "center",
      block_inset: "default",
      margin_vertical: 20,
      extra_lines_below: 0,
    }),
    preset("side-by-side", "Side-by-Side", "Left · 2 columns", {
      text_alignment: "left",
      columns: 2,
      column_width: 50,
      horizontal_gap: 20,
      balance_columns: null,
      block_inset: "sidebyside",
      margin_vertical: 20,
      extra_lines_below: 0,
    }),
    preset("right-pair", "Right Pair", "2 columns · extra bottom", {
      text_alignment: "right",
      columns: 2,
      column_width: 50,
      horizontal_gap: 20,
      balance_columns: null,
      block_inset: "default",
      margin_vertical: 20,
      extra_lines_below: 13,
    }),
  ],
  logo: [
    preset("compact", "Compact", "40% block width", {
      column_width: 40,
    }),
    preset("medium", "Medium", "65% block width", {
      column_width: 65,
    }),
    preset("full", "Full Width", "100% block width", {
      column_width: 100,
    }),
  ],
};

const FALLBACK_PRESETS = [
  preset("narrow", "Narrow", "60% block width", { column_width: 60 }),
  preset("full", "Full Width", "100% block width", { column_width: 100 }),
];

export const getLayoutPresets = (blockType) =>
  PRESETS[blockType?.toLowerCase()] || FALLBACK_PRESETS;

export const mergeLayoutPresetOptions = (options, values) => ({
  ...(options || {}),
  ...(values || {}),
});

export const isLayoutPresetActive = (options, values) =>
  Object.entries(values || {}).every(([key, value]) => options?.[key] === value);

export const isPresetTypographyActive = (fontStyle, typography) =>
  !typography ||
  Object.entries(typography).every(([styleKey, properties]) =>
    Object.entries(properties || {}).every(
      ([property, value]) => fontStyle?.[styleKey]?.[property] === value,
    ),
  );
